"use client";

import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Fade,
} from "@mui/material";
import { Star, Close, EmojiEvents } from "@mui/icons-material";

export const YearInReviewLoader = ({ isClient }: { isClient: boolean }) => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: "#0a0a0a",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        backgroundImage:
          "radial-gradient(circle at 50% 50%, rgba(236, 72, 153, 0.15) 0%, transparent 50%)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 4,
        }}
      >
        <CircularProgress
          size={120}
          thickness={2}
          sx={{
            color: "#ec4899",
            position: "absolute",
          }}
        />
        <Star
          sx={{
            fontSize: 50,
            color: "#06b6d4",
            animation: "pulse 2s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { transform: "scale(1)", opacity: 0.8 },
              "50%": { transform: "scale(1.2)", opacity: 1 },
            },
          }}
        />
      </Box>

      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          background: "linear-gradient(135deg, #ec4899 0%, #06b6d4 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          my: 4,
          animation: "fadeIn 1.5s ease-in-out infinite alternate",
          "@keyframes fadeIn": {
            "0%": { opacity: 0.5 },
            "100%": { opacity: 1 },
          },
        }}
      >
        Preparing Your Year in Review...
      </Typography>
      <Button
        variant="contained"
        onClick={() => setModalOpen(true)}
        sx={{
          background: "linear-gradient(135deg, #ec4899 0%, #06b6d4 100%)",
          color: "white",
          px: 4,
          py: 1.5,
          borderRadius: 3,
          fontWeight: 700,
          textTransform: "none",
          fontSize: "1.1rem",
          transition: "all 0.3s ease-in-out",
          boxShadow: "0 4px 15px rgba(236, 72, 153, 0.3)",
          "&:hover": {
            transform: "scale(1.05) translateY(-2px)",
            boxShadow: "0 8px 25px rgba(236, 72, 153, 0.5)",
            background: "linear-gradient(135deg, #db2777 0%, #0891b2 100%)",
          },
        }}
      >
        Show my 2025 wins
      </Button>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        TransitionComponent={Fade}
        transitionDuration={500}
        PaperProps={{
          sx: {
            bgcolor: "#111",
            backgroundImage:
              "linear-gradient(to bottom, rgba(236, 72, 153, 0.05), transparent)",
            color: "white",
            borderRadius: 4,
            border: "1px solid rgba(255, 255, 255, 0.1)",
            maxWidth: "450px",
            textAlign: "center",
            position: "relative",
            overflow: "visible",
          },
        }}
      >
        <IconButton
          onClick={() => setModalOpen(false)}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "rgba(255,255,255,0.5)",
            "&:hover": { color: "white" },
          }}
        >
          <Close />
        </IconButton>

        <DialogContent sx={{ p: 5 }}>
          <Box sx={{ mb: 3 }}>
            <EmojiEvents
              sx={{
                fontSize: 60,
                color: "#f59e0b",
                filter: "drop-shadow(0 0 10px rgba(245, 158, 11, 0.5))",
              }}
            />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: "linear-gradient(135deg, #fff 0%, #aaa 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            2025 was incredible!
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.7, mb: 4 }}
          >
            {!isClient ? (
              <>
                You didn’t just help your clients grow, you changed lives. We’ve
                rounded up your biggest wins — from your{" "}
                <strong>Strongest Lifters</strong> and{" "}
                <strong>Nutrition</strong> champions to your most incredible{" "}
                <strong>client</strong> transformations.
                <br />
                <br />
                Take a moment to celebrate how far you've come as a coach.
              </>
            ) : (
              <>
                You didn’t just grow stronger, you transformed your life. We’ve
                rounded up your biggest wins — from being a{" "}
                <strong>Strong Lifter</strong> and{" "}
                <strong>Nutrition Champion</strong> to your most incredible{" "}
                <strong>transformation</strong>.
                <br />
                <br />
                Take a moment to celebrate how far you've come in 2025.
              </>
            )}
          </Typography>

          <Button
            fullWidth
            variant="contained"
            onClick={() => setModalOpen(false)}
            sx={{
              background: "linear-gradient(135deg, #ec4899 0%, #06b6d4 100%)",
              color: "white",
              py: 2,
              borderRadius: 3,
              fontWeight: 800,
              fontSize: "1rem",
              "&:hover": {
                background: "linear-gradient(135deg, #db2777 0%, #0891b2 100%)",
              },
            }}
          >
            Let's Go!
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
